import React, { useEffect } from 'react';
import classnames from 'classnames';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { PLACEHOLDER_REGEX } from 'lib/buildAffiliateLink';
import { LazyBacon } from 'components/packages/Bacon';
import { useVertical } from 'lib/Hooks';
import { useInView } from 'react-intersection-observer';
import { stub as $t } from '@nbcnews/analytics-framework';
import { useAmpEmbedResize } from 'lib/Hooks/useAmpEmbedResize';
import { Carousel } from './Carousel';

$t('register', 'mbt_curatedlist_inview');

/**
 * Updates a URL parameter with a new value.
 * @param {string} url - The URL to be updated.
 * @param {string} param - The parameter name to update.
 * @param {string} value - The new value for the parameter.
 * @returns {string} - The updated URL.
 */
function updateParam(url, param, value) {
  try {
    const urlObj = new URL(decodeURIComponent(url));
    urlObj.searchParams.set(param, value);
    return urlObj.toString();
  } catch (e) {
    return url;
  }
}


/**
 * Renders a curated list component with various presentation styles.
 * @param {object} props - The properties passed to the component.
 * @param {string} props.id - The unique identifier for the curated list.
 * @param {string} props.title - The title of the curated list.
 * @param {string} props.description - The description of the curated list.
 * @param {object} props.presentation - The presentation style of the curated list.
 * @param {string} props.curatedListTrackingId - The tracking ID for the curated list.
 * @param {string} props.articleTrackingId - The tracking ID for the article.
 * @param {Array} props.items - The items to be displayed in the curated list.
 * @param {string|null} [props.disclaimerOverride=null] - Optional disclaimer override text.
 * @param {string|null} [props.articleId=null] - Optional article ID.
 * @param {boolean} [props.insertEndmark=false] - Whether to insert an endmark.
 * @param {string} [props.className=''] - Additional class names for the component.
 * @param {boolean} [props.ecommerceEnabled=false] - Whether e-commerce is enabled.
 * @param {string|null} [props.shortDisclaimer=null] - Optional short disclaimer text.
 * @param {string|null} [props.elementId=null] - Optional element ID for the anchor.
 */
export function CuratedList({
  id,
  title,
  description,
  presentation,
  curatedListTrackingId,
  articleTrackingId,
  items,
  disclaimerOverride = null,
  articleId = null,
  insertEndmark = false,
  className = '',
  ecommerceEnabled = false,
  shortDisclaimer = null,
  elementId = null,
}) {
  useAmpEmbedResize();
  const { style } = presentation || {};
  const { vertical } = useVertical();

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) $t('track', 'mbt_curatedlist_inview', { id, action: 'impression', presentation: style || 'PRODUCT_BACON' });
  }, [inView]);

  const { 'curated-list-presentation-styles': curatedListPresentationStyles } = useFeatureFlagContext();

  if (!items) return null;

  // The trackingId comes from Bento API with the Curated List ID appended to it.
  // So, here we replace the Curated List ID with the Article ID.
  const sanitizedCuratedListTrackingId = (id && curatedListTrackingId?.includes(id))
    ? curatedListTrackingId.replace(id, '')
    : curatedListTrackingId || '';

  /**
   * Generates the offer URL with updated tracking parameters.
   * @param {object} offer - The offer object containing external URL and other details.
   * @returns {string} - The updated offer URL.
   */
  const getOfferUrl = (offer) => {
    let offerUrl = offer.externalUrl || '';

    const completeTrackingId = curatedListTrackingId
      ? `${sanitizedCuratedListTrackingId}${articleTrackingId}`
      : articleTrackingId;

    // The URL sometimes comes from Bento API with multiple SREF keys.
    // Here, we remove all SREF keys and values, then append a new SREF with
    // a computed value.
    if (offerUrl.includes('go.redirectingat')) {
      const path = vertical === 'today' ? 'shop' : 'select';
      const domain = vertical === 'select' ? 'nbcnews' : vertical;
      const skimLinkSref = `https://www.${domain}.com/${path}/${completeTrackingId}`;
      offerUrl = updateParam(offerUrl, 'sref', skimLinkSref);
    }

    if (!articleId) return offerUrl;

    // Replace the Affiliate Link placeholder text
    if (PLACEHOLDER_REGEX.test(offerUrl)) {
      offerUrl = offerUrl.replace(PLACEHOLDER_REGEX, completeTrackingId);
      offerUrl = offerUrl.replace(id, '');
    }
    // Replace the Curated List ID with the Article ID in any remaining instances
    // within the URL
    if (id && offerUrl.includes(id)) {
      offerUrl = offerUrl.replace(curatedListTrackingId, completeTrackingId);
    }
    return offerUrl;
  };

  const productBacon = (
    <div
      ref={ref}
      data-testid="curated-list"
      className={classnames('no-slanted-bg', className, { endmark: insertEndmark })}
      id={elementId && `anchor-${elementId}`}
    >
      <LazyBacon
        vertical={vertical}
        shouldShowAdditionalSeller
        inlineArticle
        content={{
          type: 'oneOneProduct',
          subType: 'oneOneProduct',
          metadata: {
            description,
            disclaimerKey: 'OVERRIDE',
            disclaimerOverride,
            ecommerceEnabled,
            title,
            componentTitle: 'curated-list',
            trackingId: sanitizedCuratedListTrackingId,
            shortDisclaimer,
            hideDisclaimerLearnMore: true,
          },
          items: items
            .filter((item) => item.type === 'product')
            .map((item) => ({
              type: item.type,
              id: item.id,
              item: {
                ...item,
                offers: item.offers.map((offer) => ({
                  ...offer,
                  externalUrl: getOfferUrl(offer),
                })),
              },
              computedValues: {
                headline: item.name,
                teaseImage: item.teaseImage,
                url: null,
              },
            })),
        }}
        useAltTitle={vertical === 'today'}
        titleBgColor="yellow"
        presentationStyle={style}
      />
    </div>
  );

  if (!curatedListPresentationStyles) {
    return productBacon;
  }

  switch (style) {
    case 'ARTICLE_LIST':
    case 'ARTICLE_LIST_NO_IMAGES':
      return (
        <div
          ref={ref}
          data-testid="curated-list"
          id={elementId && `anchor-${elementId}`}
        >
          <Carousel
            vertical={vertical}
            items={items}
            title={title}
            hasImages={style === 'ARTICLE_LIST'}
            activityMapComponentTitle={style === 'ARTICLE_LIST' ? 'curated-list-article-list' : 'curated-list-article-list-no-images'}
            icid="curated-list"
            presentationStyle={style}
          />
        </div>
      );
    default:
      return productBacon;
  }
}


